import { ApolloClient, from, gql, InMemoryCache } from "@apollo/client"
import { CombinedGraphQLErrors } from "@apollo/client/errors"
import { ErrorLink } from "@apollo/client/link/error"
import { SchemaLink } from "@apollo/client/link/schema"
import { getServerSession } from "next-auth"
import { createClient } from "next-sanity"

import { AppSettings } from "@/settings"

import { schema } from "./schema"

const schemaLinkSanityClient = createClient(AppSettings.sanityConfigByLang())

/**
 * Creates a server-side Apollo client using SchemaLink
 * This executes queries directly against the local GraphQL schema
 */
function createServerClient() {
  const errorLink = new ErrorLink(({ error, operation }) => {
    const operationName = operation.operationName || "anonymous"

    if (CombinedGraphQLErrors.is(error)) {
      error.errors.forEach((graphQLError) => {
        const isVersionConflict = graphQLError.message?.includes("different version")

        if (isVersionConflict) {
          console.error(`[GraphQL VERSION_CONFLICT] ${operationName}:`, {
            message: graphQLError.message,
            path: graphQLError.path?.join("."),
          })
        } else {
          console.error(`[GraphQL ERROR] ${operationName}:`, {
            message: graphQLError.message,
            path: graphQLError.path?.join("."),
          })
        }
      })
    } else {
      console.error(`[Network ERROR] ${operationName}:`, error.message)
    }
  })

  const schemaLink = new SchemaLink({
    schema,
    context: async () => {
      let userData = {}
      let loggedInAdm

      try {
        const authOptions = require("@/utils/options/auth").authOptions
        const { user } = await getServerSession(authOptions)
        userData = user

        const getLoggedInAdmFromCookie = require("@/utils/cookies/serverCookies").getLoggedInAdmFromCookie
        loggedInAdm = await getLoggedInAdmFromCookie()
      } catch {
        console.warn("Using server side apollo client before completing code generation by genStatics.js.")
      }
      userData = { ...userData, loggedInAdm }

      return {
        sanityClient: schemaLinkSanityClient,
        user: userData,
      }
    },
  })

  return new ApolloClient({
    link: from([errorLink, schemaLink]),
    cache: new InMemoryCache(),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: "no-cache",
      },
      query: {
        fetchPolicy: "no-cache",
      },
    },
  })
}

/**
 * Wraps the Apollo client to serialize query results to plain objects.
 * This is required for RSC compatibility - Apollo returns objects with
 * special prototypes that can't be passed from Server to Client Components.
 */
function createSerializingClient(client) {
  return {
    ...client,
    query: async (...args) => {
      const result = await client.query(...args)
      // Serialize data to plain objects for RSC compatibility
      return {
        ...result,
        data: result.data ? JSON.parse(JSON.stringify(result.data)) : result.data,
      }
    },
    mutate: async (...args) => {
      const result = await client.mutate(...args)
      return {
        ...result,
        data: result.data ? JSON.parse(JSON.stringify(result.data)) : result.data,
      }
    },
  }
}

/**
 * Get the server-side Apollo client for RSC data fetching.
 * Uses SchemaLink to execute queries directly against the local schema.
 * Results are automatically serialized to plain objects for RSC compatibility.
 */
function getServerClient() {
  return createSerializingClient(createServerClient())
}

export { getServerClient, gql }
